import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Heart, Minus, Plus, Truck, RotateCcw, ShieldCheck, ShoppingCart, Zap, ChevronRight, BadgePercent } from 'lucide-react';
import { toast } from 'sonner';
import { shopApi, useShop } from '../lib/shop';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import ProductCard, { PriceRow, OfferBadge, isAvailable } from '../components/ProductCard';
import { NO_FEES_LINE, THEMES } from '../lib/catalog';
import '../styles/shop.css';

const Shell = ({ children }) => (
  <div className="si-page"><ShopNavbar /><div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-6">{children}</div><ShopFooter /></div>
);

const Gallery = ({ p, img, setImg }) => (
  <div className="md:sticky md:top-36 h-fit flex flex-col-reverse md:flex-row gap-3">
    {p.images?.length > 1 && (
      <div className="flex md:flex-col gap-2">
        {p.images.map((src, i) => (
          <button key={src} onClick={() => setImg(i)} onMouseEnter={() => setImg(i)} data-testid={`pdp-thumb-${i}`}
            className={`w-16 h-20 rounded overflow-hidden border-2 ${i === img ? 'border-[var(--si-blue)]' : 'border-[var(--si-line)]'}`}>
            <img src={src} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    )}
    <div className="flex-1 aspect-[3/4] bg-[var(--si-bg-soft)] rounded overflow-hidden border border-[var(--si-line)]">
      <img src={p.images?.[img] || p.images?.[0]} alt={p.name} className="w-full h-full object-cover" data-testid="pdp-main-image" />
    </div>
  </div>
);

const Chips = ({ label, items, value, onChange, prefix, extra }) => (
  <div className="mt-6">
    <p className="text-sm font-bold mb-2">{label} {extra}</p>
    <div className="flex flex-wrap gap-2" data-testid={`pdp-${prefix}s`}>
      {items.map((s) => (
        <button key={s} onClick={() => onChange(s)} data-testid={`${prefix}-${s}`}
          className={`min-w-[48px] px-4 py-2 rounded border-2 text-sm font-semibold transition-colors ${value === s ? 'border-[var(--si-blue)] text-[var(--si-blue)] bg-[#EEF2FB]' : 'border-[#C7CCD6] text-[var(--si-black)] hover:border-[var(--si-blue)]'}`}>
          {s}
        </button>
      ))}
    </div>
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [img, setImg] = useState(0);
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const { refreshCart, wishlistSlugs, refreshWishlist } = useShop();

  useEffect(() => {
    setData(null);
    shopApi.getProduct(slug).then((d) => {
      setData(d); setSize(d.product.sizes?.[0] || ''); setColor(d.product.colors?.[0] || ''); setImg(0); setQty(1);
    }).catch(() => setData({ error: true }));
  }, [slug]);

  if (!data) return <Shell><p className="si-card p-10 text-center" data-testid="pdp-loading">Loading…</p></Shell>;
  if (data.error) return (
    <Shell><div className="si-card p-10 text-center" data-testid="pdp-not-found"><p className="text-2xl font-bold">Product not found</p><Link to="/shop" className="mt-3 inline-block text-[var(--si-blue)] font-semibold underline">Back to shop</Link></div></Shell>
  );

  const p = data.product;
  const inWish = wishlistSlugs?.includes(p.slug);
  const available = isAvailable(p);
  const addToCart = async (buyNow) => {
    if (adding) return;
    setAdding(true);
    try {
      await shopApi.addToCart(p.slug, size, color, qty);
      await refreshCart();
      if (buyNow) nav('/checkout');
      else toast.success('Added to cart', { description: `${p.name} · ${[size, color].filter(Boolean).join(' · ')}` });
    } catch (e) { toast.error(e.message); } finally { setAdding(false); }
  };
  const toggleWish = async () => {
    try { await shopApi.toggleWishlist(p.slug); refreshWishlist(); }
    catch { window.location.href = '/login'; }
  };

  return (
    <Shell>
      <nav className="flex items-center gap-1 text-sm text-[var(--si-grey)] mb-3" data-testid="pdp-breadcrumb">
        <Link to="/" className="hover:text-[var(--si-blue)]">Home</Link><ChevronRight className="w-3.5 h-3.5" />
        <Link to="/shop" className="hover:text-[var(--si-blue)]">Shop</Link><ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/shop/${p.collection}`} className="hover:text-[var(--si-blue)] capitalize">{p.collection}</Link>
      </nav>
      <div className="si-card p-4 md:p-6 grid md:grid-cols-2 gap-6 md:gap-10">
        <div>
          <Gallery p={p} img={img} setImg={setImg} />
          {!available ? (
            <div className="mt-4 rounded bg-[#FEF2F2] border border-[#FCA5A5] p-4 text-center" data-testid="pdp-out-of-stock">
              <p className="text-lg font-bold text-[#B91C1C]">Currently Out of Stock</p>
              <p className="text-sm text-[var(--si-black)]">Add it to your wishlist and check back soon.</p>
            </div>
          ) : (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => addToCart(false)} disabled={adding} className="si-btn si-btn-cart w-full !py-4 text-base" data-testid="pdp-add-to-bag">
              <ShoppingCart className="w-5 h-5" /> {adding ? 'Adding…' : 'ADD TO CART'}
            </button>
            <button onClick={() => addToCart(true)} disabled={adding} className="si-btn si-btn-buy w-full !py-4 text-base" data-testid="pdp-buy-now">
              <Zap className="w-5 h-5" /> BUY NOW
            </button>
          </div>
          )}
        </div>

        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-[var(--si-grey)]">{p.category}{p.theme ? ` · ${THEMES[p.theme]?.title}` : ''}</p>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold leading-tight" data-testid="pdp-name">{p.name}</h1>
          <p className="mt-4 inline-block bg-[#E11D48] text-white text-xs font-extrabold tracking-widest px-2 py-0.5 rounded-sm">SALE PRICE</p>
          <div className="mt-2" data-testid="pdp-price"><PriceRow p={p} size="lg" /></div>
          <OfferBadge offer={p.offer} className="mt-2 !text-sm" />
          {p.coin_buttons && (
            <p className="mt-3 flex items-center gap-2 rounded bg-[#FEF3C7] border border-[#FCD34D] px-3 py-2 text-sm font-semibold text-[#92400E]" data-testid="pdp-coin-buttons">
              <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#F6D365] to-[#B8860B] border border-[#92400E]/40 shrink-0" />
              Buttons made from original historical Indian coins
            </p>
          )}
          <p className="mt-2 flex items-start gap-2 text-sm font-semibold text-[var(--si-green)]" data-testid="pdp-no-fees"><BadgePercent className="w-4 h-4 mt-0.5 shrink-0" />{NO_FEES_LINE}</p>

          {p.sizes?.length > 0 && <Chips label="Select Size" items={p.sizes} value={size} onChange={setSize} prefix="size"
            extra={<Link to="/size-guide" className="ml-2 text-sm font-semibold text-[var(--si-blue)]">Size guide</Link>} />}
          {p.colors?.length > 0 && <Chips label="Colour" items={p.colors} value={color} onChange={setColor} prefix="color" />}

          <div className="mt-6 flex items-center gap-4">
            <p className="text-sm font-bold">Quantity</p>
            <div className="flex items-center border border-[#C7CCD6] rounded" data-testid="pdp-qty">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:bg-[var(--si-bg-soft)]" aria-label="Decrease" data-testid="pdp-qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-semibold" data-testid="pdp-qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:bg-[var(--si-bg-soft)]" aria-label="Increase" data-testid="pdp-qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={toggleWish} data-testid="pdp-wishlist"
              className="ml-auto flex items-center gap-2 text-sm font-semibold text-[var(--si-black)] hover:text-[#E11D48]">
              <Heart className={`w-5 h-5 ${inWish ? 'fill-[#E11D48] text-[#E11D48]' : ''}`} /> {inWish ? 'Wishlisted' : 'Wishlist'}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 border border-[var(--si-line)] rounded p-3">
            {[[Truck, 'Free delivery all over India on ₹799+'], [RotateCcw, '7-day no-questions replacement'], [ShieldCheck, 'Secure payments']].map(([Icon, t]) => (
              <div key={t} className="flex flex-col items-center text-center gap-1.5 text-xs font-medium text-[var(--si-black)]"><Icon className="w-5 h-5 text-[var(--si-navy)]" />{t}</div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-[var(--si-line)]">
            <p className="text-base font-bold mb-2">Product details</p>
            <p className="text-[15px] text-[var(--si-black)] leading-relaxed" data-testid="pdp-description">{p.description}</p>
          </div>
          {p.story && (
            <div className="mt-5 p-4 rounded bg-[#FFF8EC] border border-[#FCE3B6]" data-testid="pdp-story">
              <p className="text-sm font-bold text-[#9A5B00] mb-1">The story behind this design</p>
              <p className="text-[15px] text-[var(--si-black)] leading-relaxed">{p.story}</p>
            </div>
          )}
        </div>
      </div>

      {data.related?.length > 0 && (
        <section className="si-card p-4 md:p-6 mt-4" data-testid="pdp-related">
          <h2 className="text-base md:text-lg font-bold mb-4">Similar products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {data.related.map((r) => <ProductCard key={r.slug} p={r} />)}
          </div>
        </section>
      )}
    </Shell>
  );
};

export default ProductDetail;
